import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  FormInput,
  AutocompleteInput,
  ConfigurationContext,
  FileInput,
  FormGetter,
  HiddenInput,
  CheckboxInput,
  ConditionalInput,
} from '@kirz/mui-admin';
import React, {
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  FILTER_SYSTEM_TYPE_ITEMS,
  FILTERS_TYPE_ITEMS,
} from 'constants/template';

export const FiltersTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { filterColumns, ...rest } = props;
    const tableRef = useRef<DataTableExRef>(null);
    const [sortValues, setSortValues] = useState<number[] | null>(null);
    const { hasura } = useContext(ConfigurationContext);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const refreshSortValues = async () => {
      const items = await hasura.request({
        type: 'query',
        source: 'filter',
        selection: 'sort',
        where: { isRemoved: { _eq: false } },
      });
      setSortValues(items.map((x: any) => x.sort));
    };

    useEffect(() => {
      refreshSortValues();
    }, []);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            width: 60,
            field: 'sort',
            type: 'sort',
            sortKey: 'sort',
            rowsSortValues: sortValues,
            onSortChange: async (direction, row, targetRowSort) => {
              const source = 'Filter';
              const { id } = row;

              await hasura.request({
                type: 'custom',
                query: `
                  mutation ChangeSort($where1: ${source}BoolExp!, $set1: ${source}SetInput!, $where2: ${source}BoolExp!, $set2: ${source}SetInput!) {
                    result2: update${source}(where: $where2, _set: $set2) {
                      affected_rows
                    }
                    result1: update${source}(where: $where1, _set: $set1) {
                      affected_rows
                    }
                  }
                `,
                variables: {
                  where1: { id: { _eq: id } },
                  set1: { sort: targetRowSort },
                  where2: {
                    sort: { _eq: targetRowSort },
                  },
                  set2: { sort: row.sort },
                },
              });

              tableRef.current?.reload();
            },
          },
          {
            field: 'iconId',
            headerName: 'Иконка',
            type: 'file',
            hideText: true,
            selector: 'file { id contentType name extension }',
            width: 80,
            sortable: false,
            valueGetter({ row }) {
              return row.file?.id;
            },
            fetchMetadata(row) {
              if (!row.file) {
                return null;
              }

              return {
                ...row.file,
                thumbnail: `/api/v1/files/w_100,h_100/${row.file.id}`,
              };
            },
          },
          {
            field: 'title',
            selector: 'title subtitle',
            headerName: 'Текст',
            flex: 1,
            valueGetter({ row }) {
              return `${row.title ?? ''} ${row.subtitle ?? ''}`.trim();
            },
          },
          {
            field: 'tags',
            selector: 'tags { tag { name } }',
            headerName: 'Теги',
            valueGetter({ row }) {
              return row.tags.map((x: any) => x.tag.name).join(', ');
            },
            flex: 1,
          },
          {
            field: 'type',
            headerName: 'Отображать',
            flex: 1,
            type: 'select',
            items: FILTERS_TYPE_ITEMS,
          },
          {
            field: 'systemType',
            headerName: 'Системный тип',
            flex: 1,
            type: 'select',
            items: FILTER_SYSTEM_TYPE_ITEMS,
          },
          {
            field: 'isNew',
            headerName: 'Новое',
            type: 'boolean',
            width: 100,
          },
          { field: 'createdAt', headerName: 'Дата создания', type: 'date' },
        ]),
      [filterColumns, sortValues],
    );

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="filter"
        columns={columns}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'sort', sort: 'asc' }}
        selectProps={{
          filter: {
            isRemoved: { _eq: false },
            ...props.selectProps?.filter,
          },
        }}
        formTitle={(isNew) => (isNew ? 'Новый фильтр' : 'Редактировать фильтр')}
        formDialogProps={{
          ...rest.formDialogProps,
          formProps: {
            defaultValues: {
              ...rest.formDialogProps?.formProps?.defaultValues,
              isNew: true,
              isPremium: false,
              type: 'image',
              systemType: 'filter',
            },
          },
          maxWidth: 'sm',
          formSubmitterProps: {
            preSubmit(item) {
              return {
                ...item,
                ...(!item.id && {
                  tags:
                    item.systemType === 'filter'
                      ? {
                          data: item.tags.map((tagId: any) => ({ tagId })),
                        }
                      : undefined,
                  id: undefined,
                }),
              };
            },
            onSubmit(item) {
              refreshSortValues();
            },
          },
        }}
        deletable={{
          onDeleted: (row) => {
            setSortValues(sortValues!.filter((x) => x !== row.sort));
          },
        }}
      >
        <HiddenInput name="id" />
        <FormInput name="title" label="Название" required md={6} />
        <FormInput name="subtitle" label="Подзаголовок" required md={6} />
        <FormGetter
          names={['id']}
          render={({ id }) => (
            <>
              <AutocompleteInput
                name="type"
                label="Отображать"
                mode="memory"
                required
                options={FILTERS_TYPE_ITEMS}
                xs={12}
                md={6}
                readOnly={!!id}
              />
              <AutocompleteInput
                name="systemType"
                label="Системный тип"
                mode="memory"
                required
                options={FILTER_SYSTEM_TYPE_ITEMS}
                xs={12}
                md={6}
                readOnly={!!id}
              />
            </>
          )}
        />

        <ConditionalInput
          deps={['systemType', 'type']}
          variants={[
            {
              if: ({ systemType }) => systemType !== 'filter',
              input: ({ type }) => (
                <AutocompleteInput
                  name="systemTemplateId"
                  label="Шаблон"
                  source="template"
                  mode="hasura"
                  selection="id name"
                  itemText="name"
                  itemValue="id"
                  required
                  filter={{
                    type: { _eq: type },
                  }}
                />
              ),
            },
          ]}
        />

        <FormGetter
          names={['id', 'systemType']}
          render={({ id, systemType }: { id: number; systemType: string }) => {
            if (systemType !== 'filter') {
              return null;
            }

            if (!id) {
              return (
                <AutocompleteInput
                  name="tags"
                  label="Теги"
                  source="tag"
                  mode="hasura"
                  selection="id name"
                  itemText="name"
                  itemValue="id"
                  multiple
                  required
                  // formFetcherValueResolver={null}
                />
              );
            }

            return (
              <AutocompleteInput
                name="tags"
                label="Теги"
                source="tag"
                mode="hasura"
                selection="id name"
                itemText="name"
                itemValue="id"
                multiple
                formFetcherValueResolver={{
                  source: 'filterTag',
                  selection: 'tagId',
                  filter: { filterId: { _eq: id } },
                  resolveItems: (items) => items.map((x) => x.tagId),
                }}
                formSubmitterValueResolver={{
                  type: 'hasura-many-to-many',
                  source: 'filterTag',
                  foreignKey: 'tagId',
                  entityId: { filterId: id },
                }}
              />
            );
          }}
        />

        <FileInput
          name="iconId"
          label="Иконка"
          dropzoneProps={{
            accept: {
              'image/*': ['.jpg', '.jpeg', '.png', '.heic', '.heif'],
            },
            validator(file) {
              if (
                file.type !== 'image/jpeg' &&
                file.type !== 'image/png' &&
                file.type !== 'image/heic' &&
                file.type !== 'image/heif'
              ) {
                return {
                  code: 'file-invalid-type',
                  message: 'Поддерживаемые форматы: JPG, JPEG, PNG, HEIC, HEIF',
                };
              }

              return null;
            },
          }}
          required
          md={6}
        />
        <CheckboxInput name="isNew" label="Новая?" xs={12} md={3} />
        <CheckboxInput name="isPremium" label="Премиум?" xs={12} md={3} />
      </DataTableEx>
    );
  },
);
